import { nextTick } from "vue"
import type { RouteRecordRaw } from "vue-router"
import { createRouter, createWebHistory } from "vue-router"
import { useAuthStore } from "@/stores/auth"
import { useConsumerAuthStore } from "@/stores/consumerAuth"
import { useMenuStore } from "@/stores/menu"

const routes: RouteRecordRaw[] = [
  /** Kompatibilitas bookmark lama */
  {
    path: "/landingpage",
    redirect: "/",
  },
  /** Legacy: login admin dulu di /login → sekarang /admin */
  {
    path: "/login",
    redirect: "/admin",
  },
  /** Bookmark lama → URL bersih (tanpa /sandbox atau /portal) */
  { path: "/sandbox/login", redirect: "/masuk" },
  { path: "/sandbox/register", redirect: "/daftar" },
  { path: "/sandbox/forgot-password", redirect: "/lupa-kata-sandi" },
  { path: "/sandbox/reset-password", redirect: "/atur-ulang-kata-sandi" },
  { path: "/sandbox/profile", redirect: "/profil" },
  { path: "/sandbox/account-activated", redirect: "/akun-aktif" },
  { path: "/sandbox/unsubscribe", redirect: "/batal-email" },
  { path: "/sandbox/onboarding-preferences", redirect: "/preferensi" },
  { path: "/sandbox/pricing", redirect: "/harga" },
  { path: "/sandbox/feeds", redirect: "/feed" },
  { path: "/portal", redirect: "/feed" },
  { path: "/portal/masuk", redirect: "/masuk" },
  { path: "/portal/daftar", redirect: "/daftar" },
  { path: "/portal/lupa-kata-sandi", redirect: "/lupa-kata-sandi" },
  { path: "/portal/atur-ulang-kata-sandi", redirect: "/atur-ulang-kata-sandi" },
  { path: "/portal/akun-aktif", redirect: "/akun-aktif" },
  { path: "/portal/preferensi", redirect: "/preferensi" },
  { path: "/portal/harga", redirect: "/harga" },
  { path: "/portal/feed", redirect: "/feed" },
  { path: "/portal/profil", redirect: "/profil" },
  { path: "/portal/batal-email", redirect: "/batal-email" },
  {
    path: "/portal/tonton/:id",
    redirect: (to) => ({ name: "ConsumerVideo", params: { id: to.params.id } }),
  },
  /** Path lama dari catalogContentPath / bookmark — redirect ke pemutar konsumen */
  {
    path: "/video/:id",
    redirect: (to) => ({ name: "ConsumerVideo", params: { id: to.params.id } }),
  },
  {
    path: "/videos/:id",
    redirect: (to) => ({ name: "ConsumerVideo", params: { id: to.params.id } }),
  },
  /**
   * Aplikasi konsumen: satu layout + anak ber-path relatif.
   * consumerSelfContained: view sudah membawa chrome sendiri (landing / katalog).
   */
  {
    path: "/",
    component: () => import("@/layouts/ConsumerLayout.vue"),
    meta: { layout: "consumer", requiresAuth: false },
    children: [
      {
        path: "",
        name: "Landing",
        component: () => import("@/views/consumer/ConsumerLandingView.vue"),
        meta: { title: "Beranda", consumerSelfContained: true },
      },
      {
        path: "series",
        name: "SeriesPageSandbox",
        component: () => import("@/views/consumer/ConsumerLandingView.vue"),
        meta: { title: "Serial", consumerSelfContained: true },
      },
      {
        path: "films",
        name: "FilmsPageSandbox",
        component: () => import("@/views/consumer/ConsumerLandingView.vue"),
        meta: { title: "Film", consumerSelfContained: true },
      },
      {
        path: "daftar-saya",
        name: "DaftarSayaPageSandbox",
        component: () => import("@/views/consumer/ConsumerLandingView.vue"),
        meta: { title: "Daftar Saya", consumerSelfContained: true },
      },
      {
        path: "masuk",
        name: "ConsumerMasuk",
        component: () => import("@/views/consumer/auth/ConsumerLoginView.vue"),
        meta: { plainLayout: true, consumerSurface: true, title: "Masuk" },
      },
      {
        path: "daftar",
        name: "ConsumerDaftar",
        component: () => import("@/views/consumer/auth/ConsumerRegisterView.vue"),
        meta: { plainLayout: true, consumerSurface: true, title: "Daftar" },
      },
      {
        path: "lupa-kata-sandi",
        name: "ConsumerLupaSandi",
        component: () => import("@/views/consumer/auth/ConsumerForgotPasswordView.vue"),
        meta: {
          plainLayout: true,
          consumerSurface: true,
          title: "Lupa kata sandi",
        },
      },
      {
        path: "atur-ulang-kata-sandi",
        name: "ConsumerResetSandi",
        component: () => import("@/views/consumer/auth/ConsumerResetPasswordView.vue"),
        meta: {
          plainLayout: true,
          consumerSurface: true,
          title: "Atur ulang kata sandi",
        },
      },
      {
        path: "akun-aktif",
        name: "ConsumerAkunAktif",
        component: () => import("@/views/consumer/ConsumerAccountActivatedView.vue"),
        meta: { plainLayout: true, consumerSurface: true, title: "Akun aktif" },
      },
      {
        path: "feed",
        name: "ConsumerFeed",
        component: () => import("@/views/consumer/ConsumerFeedView.vue"),
        meta: { plainLayout: true, consumerAuth: true, consumerSurface: true, title: "Feed" },
      },
      {
        path: "preferensi",
        name: "ConsumerPreferensi",
        component: () => import("@/views/consumer/ConsumerOnboardingPreferencesView.vue"),
        meta: {
          plainLayout: true,
          consumerAuth: true,
          consumerSurface: true,
          title: "Preferensi minat",
        },
      },
      {
        path: "harga",
        name: "ConsumerHarga",
        component: () => import("@/views/consumer/ConsumerPricingView.vue"),
        meta: { plainLayout: true, consumerSurface: true, title: "Harga & paket" },
      },
      {
        path: "profil",
        name: "ConsumerProfil",
        component: () => import("@/views/consumer/ConsumerProfileView.vue"),
        meta: { plainLayout: true, consumerAuth: true, consumerSurface: true, title: "Profil" },
      },
      {
        path: "invoice/:paymentId",
        name: "ConsumerInvoice",
        component: () => import("@/views/consumer/ConsumerInvoiceView.vue"),
        meta: { plainLayout: true, consumerAuth: true, consumerSurface: true, title: "Invoice" },
      },
      {
        path: "cari",
        name: "ConsumerSearch",
        component: () => import("@/views/consumer/ConsumerLandingView.vue"),
        meta: { consumerSelfContained: true, consumerSurface: true, title: "Hasil pencarian" },
      },
      {
        path: "notifikasi",
        name: "ConsumerNotificationsInbox",
        component: () => import("@/views/shared/NotificationsInboxView.vue"),
        meta: { plainLayout: true, consumerAuth: true, consumerSurface: true, title: "Notifikasi" },
      },
      {
        path: "notifikasi/:notificationId",
        name: "ConsumerNotificationDetail",
        component: () => import("@/views/admin/NotificationDetailView.vue"),
        meta: {
          plainLayout: true,
          consumerAuth: true,
          consumerSurface: true,
          title: "Detail notifikasi",
        },
      },
      {
        path: "batal-email",
        name: "ConsumerBatalEmail",
        component: () => import("@/views/consumer/ConsumerUnsubscribeView.vue"),
        meta: { consumerAuth: true, consumerSurface: true, title: "Berhenti email" },
      },
      {
        path: "tonton/:id",
        name: "ConsumerVideo",
        component: () => import("@/views/shared/VideoPlayerView.vue"),
        /** Tamu boleh buka halaman; premium / episode berbayar dikunci lewat playback_locked di BE + UI */
        meta: { plainLayout: true, consumerSurface: true, title: "Putar" },
      },
    ],
  },
  /** Panel administrator: semua rute di bawah /admin/ */
  {
    path: "/admin",
    component: () => import("@/layouts/AdminLayout.vue"),
    /** Parent tidak memaksa auth agar halaman login (anak) bisa publik */
    meta: { requiresAuth: false },
    children: [
      {
        path: "",
        name: "Login",
        component: () => import("@/views/admin/LoginView.vue"),
        meta: {
          requiresAuth: false,
          title: "Login",
          hideAdminChrome: true,
        },
      },
      {
        path: "dashboard",
        name: "Dashboard",
        component: () => import("@/views/admin/DashboardView.vue"),
        meta: { requiresAuth: true, title: "Dashboard", fullWidth: true },
      },
      {
        path: "comments",
        name: "CommentModeration",
        component: () => import("@/views/admin/CommentModerationView.vue"),
        meta: { requiresAuth: true, title: "Moderasi komentar", fullWidth: true },
      },
      {
        path: "users",
        name: "Users",
        component: () => import("@/views/admin/UserManagementView.vue"),
        meta: { requiresAuth: true, title: "Pengguna", fullWidth: true },
      },
      {
        path: "staff",
        name: "Staff",
        component: () => import("@/views/admin/StaffManagementView.vue"),
        meta: { requiresAuth: true, title: "Pengurus", fullWidth: true },
      },
      {
        path: "users/:userId",
        name: "PublicProfile",
        component: () => import("@/views/admin/PublicProfileView.vue"),
        meta: { requiresAuth: true, title: "Profil pengguna" },
      },
      {
        path: "videos",
        name: "Videos",
        component: () => import("@/views/admin/VideoManagementView.vue"),
        meta: { requiresAuth: true, title: "Video Management", fullWidth: true },
      },
      {
        path: "produksi",
        name: "ProductionList",
        component: () => import("@/views/admin/production/ProductionListView.vue"),
        meta: { requiresAuth: true, title: "Produksi Film", fullWidth: true },
      },
      {
        path: "produksi/new",
        name: "ProductionCreate",
        component: () => import("@/views/admin/production/ProductionCreateView.vue"),
        meta: { requiresAuth: true, title: "Produksi Film baru", fullWidth: true },
      },
      {
        path: "produksi/:id",
        name: "ProductionDetail",
        component: () => import("@/views/admin/production/ProductionDetailView.vue"),
        meta: { requiresAuth: true, title: "Detail Produksi", fullWidth: true },
      },
      {
        path: "billing",
        name: "BillingAdmin",
        component: () => import("@/views/admin/BillingAdminView.vue"),
        meta: { requiresAuth: true, title: "Harga & koin", fullWidth: true },
      },
      {
        path: "videos/new",
        redirect: { name: "VideoCreateSingle" },
      },
      {
        path: "videos/new/single",
        name: "VideoCreateSingle",
        component: () => import("@/views/admin/VideoCreateView.vue"),
        meta: { requiresAuth: true, title: "Tambah Single Video", fullWidth: true },
      },
      {
        path: "series/new",
        name: "SeriesCreate",
        component: () => import("@/views/admin/VideoCreateView.vue"),
        meta: { requiresAuth: true, title: "Tambah Video Series", fullWidth: true },
      },
      {
        path: "videos/:id/edit",
        name: "VideoEdit",
        component: () => import("@/views/admin/VideoEditView.vue"),
        meta: { requiresAuth: true, title: "Edit video", fullWidth: true },
      },
      {
        path: "series/:seriesId/edit",
        name: "SeriesEdit",
        component: () => import("@/views/admin/VideoCreateView.vue"),
        meta: { requiresAuth: true, title: "Edit serial", fullWidth: true },
      },
      {
        path: "feed",
        name: "Feed",
        component: () => import("@/views/admin/FeedView.vue"),
        meta: { requiresAuth: true, title: "Feed Mengikuti", fullWidth: true },
      },
      {
        path: "discovery",
        name: "Discovery",
        component: () => import("@/views/admin/DiscoveryView.vue"),
        meta: { requiresAuth: true, title: "Discovery", fullWidth: true },
      },
      {
        path: "videos/:id",
        name: "VideoPlayer",
        component: () => import("@/views/shared/VideoPlayerView.vue"),
        meta: { requiresAuth: true, title: "Video Player", fullWidth: true },
      },
      {
        path: "analytics",
        name: "Analytics",
        component: () => import("@/views/admin/AnalyticsView.vue"),
        meta: { requiresAuth: true, title: "Analytics", fullWidth: true },
      },
      {
        path: "settings",
        name: "Settings",
        component: () => import("@/views/admin/SettingsView.vue"),
        meta: { requiresAuth: true, title: "Settings" },
      },
      {
        path: "notifications",
        name: "NotificationsInbox",
        component: () => import("@/views/shared/NotificationsInboxView.vue"),
        meta: { requiresAuth: true, title: "Notifikasi", fullWidth: true },
      },
      {
        path: "notifications/:notificationId",
        name: "NotificationDetail",
        component: () => import("@/views/admin/NotificationDetailView.vue"),
        meta: { requiresAuth: true, title: "Detail notifikasi" },
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/views/shared/NotFoundView.vue"),
    meta: { requiresAuth: false, title: "Page Not Found" },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()
  const consumerAuthStore = useConsumerAuthStore()
  // Hanya halaman admin yang set meta.requiresAuth: true; jangan pakai !== false (anak konsumen tanpa meta ikut kena redirect ke /admin).
  const requiresAdminAuth = to.matched.some((record) => record.meta.requiresAuth === true)
  const needsConsumerAuth = to.matched.some(
    (record) => record.meta.consumerAuth === true || record.meta.portalAuth === true,
  )

  if (to.meta.title) {
    const isAdminArea = to.path.startsWith("/admin")
    const suffix = isAdminArea ? "Skrool TV Admin" : "Skrool TV"
    document.title = `${to.meta.title} - ${suffix}`
  }

  if (needsConsumerAuth) {
    await consumerAuthStore.checkAuth()
    if (!consumerAuthStore.token) {
      next({ name: "ConsumerMasuk", query: { redirect: to.fullPath } })
      return
    }
  }

  if (to.name === "ConsumerMasuk") {
    await consumerAuthStore.checkAuth()
    if (consumerAuthStore.token) {
      const redirectTarget = typeof to.query.redirect === "string" ? to.query.redirect : "/"
      next(redirectTarget)
      return
    }
  }

  if (requiresAdminAuth) {
    if (!authStore.isAuthenticated) {
      await authStore.checkAuth()
    }

    if (!authStore.isAuthenticated) {
      next({ name: "Login", query: { redirect: to.fullPath } })
      return
    }

    const adminRole = (authStore.user?.role ?? "").toLowerCase()
    if (authStore.user && adminRole !== "superadmin" && adminRole !== "admin") {
      next({ name: "Dashboard" })
      return
    }
  }

  /** Sudah login → jangan tampilkan form login admin lagi */
  if (to.name === "Login") {
    if (!authStore.isAuthenticated) {
      await authStore.checkAuth()
    }
    if (authStore.isAuthenticated) {
      next({ name: "Dashboard" })
      return
    }
  }

  next()
})

router.afterEach((to) => {
  // Tunda ke tick berikutnya agar Pinia sudah terpasang di konteks aplikasi (setelah router.install).
  nextTick(() => {
    useMenuStore().initMenu(to.path)
  })
})

export default router
